/**
 * WO-D2.M — set-password invite (PUBLIC). The owner/invited user opens this from the emailed
 * link: beam://accept-invite?token=… (or the web equivalent). They set a password; the backend
 * returns a session, which we store → land in the app's first-run. No @smartbox/* (invariant 1).
 *
 * WO-M0 — rebuilt on the design system: Screen / Field / Button / Text.
 */
import { useState } from 'react';
import { KeyboardAvoidingView, Platform, StyleSheet, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { acceptInvite } from '../src/api/onboarding';
import { useSession } from '../src/auth/session';
import { ApiRequestError } from '../src/api/client';
import { Screen, Text, Field, Button } from '../src/components/ui';
import { BeamLogo } from '../src/components/brand/Logo';
import { theme } from '../src/theme';

export default function AcceptInviteScreen() {
  const params = useLocalSearchParams<{ token?: string }>();
  const token = typeof params.token === 'string' ? params.token : '';
  const applyLogin = useSession((s) => s.applyLogin);
  const router = useRouter();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit() {
    setError(null);
    if (!token) return setError('This invite link is missing its token.');
    if (password.length < 8) return setError('Password must be at least 8 characters.');
    if (password !== confirm) return setError('Passwords do not match.');
    setBusy(true);
    try {
      const session = await acceptInvite(token, password);
      await applyLogin(session);
      router.replace('/(app)/onboarding');
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : 'Could not accept the invite.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Screen>
      <KeyboardAvoidingView
        style={styles.flex}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <View style={styles.container}>
          <View style={styles.brand}>
            <BeamLogo size={40} />
            <Text variant="title">Welcome to BEAM</Text>
            <Text variant="subhead" color="secondary">
              Set a password to activate your account.
            </Text>
          </View>

          <View style={styles.form}>
            <Field
              label="New password"
              value={password}
              onChangeText={setPassword}
              secureToggle
              autoCapitalize="none"
              textContentType="newPassword"
              autoComplete="new-password"
              placeholder="At least 8 characters"
              editable={!busy}
              accessibilityLabel="New password"
              returnKeyType="next"
            />

            <Field
              label="Confirm password"
              value={confirm}
              onChangeText={setConfirm}
              secureToggle
              autoCapitalize="none"
              textContentType="newPassword"
              autoComplete="new-password"
              placeholder="Repeat password"
              editable={!busy}
              accessibilityLabel="Confirm password"
              returnKeyType="done"
              onSubmitEditing={onSubmit}
              error={error ?? undefined}
            />

            <Button
              title="Activate account"
              onPress={onSubmit}
              loading={busy}
              disabled={busy}
              size="lg"
              style={styles.submit}
              accessibilityLabel="Activate account"
            />

            <Button
              title="Already have a password? Sign in"
              variant="ghost"
              size="sm"
              onPress={() => router.replace('/login')}
              accessibilityLabel="Go to sign in"
            />
          </View>
        </View>
      </KeyboardAvoidingView>
    </Screen>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1 },
  container: { flex: 1, justifyContent: 'center', gap: theme.spacing['4xl'] },
  brand: { alignItems: 'center', gap: theme.spacing.md },
  form: { gap: theme.spacing.md },
  submit: { marginTop: theme.spacing.sm },
});
